import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1512100356356-de1b84283e18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxwcml2YXRlJTIwdHJhdmVsJTIwY2x1YnxlbnwwfHx8fDE3ODM2NDMyMjF8MA&ixlib=rb-4.1.0&q=85";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--ir-bg)]" data-testid="login-page">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ir-blue-dark)]/70 via-[var(--ir-blue-dark)]/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-4xl leading-tight text-white">Your private gateway to<br /><span className="text-gradient-orange italic">hidden member rates.</span></h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <Link to="/"><Logo size="md" /></Link>
          <h1 className="font-display text-4xl mt-10 text-[var(--ir-text)]">Member Login</h1>
          <p className="text-[var(--ir-muted)] mt-2">Sign in to access your club portal.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-[var(--ir-text)]">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-2 bg-white border-[var(--ir-border)] rounded-none h-12 focus-visible:ring-[var(--ir-blue)]" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[var(--ir-text)]">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-2 bg-white border-[var(--ir-border)] rounded-none h-12 focus-visible:ring-[var(--ir-blue)]" data-testid="login-password-input" />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-sm text-[var(--ir-blue)] hover:underline" data-testid="login-forgot-password-link">Forgot password?</Link>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none h-12" data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" />Sign In</>}
            </Button>
          </form>

          <p className="text-[var(--ir-muted)] mt-8 text-sm">
            Not a member yet?{" "}
            <Link to="/register" className="text-[var(--ir-blue)] font-medium hover:underline" data-testid="login-to-register-link">Request access</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
